import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';
import M45NavPills from '../components/M45NavPills';
import ProjectSearchModal from '../components/ProjectTracking/ProjectSearchModal';
import CreateListModal from '../components/ProjectTracking/CreateListModal';
import ProjectTabView from '../components/ProjectTracking/ProjectTabView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectTrackingPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);
  const [activeList, setActiveList] = useState(null);
  const [loadingLists, setLoadingLists] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [showCreateList, setShowCreateList] = useState(false);
  const [editingList, setEditingList] = useState(null); // list to edit
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [projectsData, setProjectsData] = useState({});
  // Prevent the list-sync effect from resetting the active tab after a project is added
  const skipTabResetRef = useRef(false);

  const fetchLists = useCallback(async () => {
    try {
      const res = await api.get(`${API}/project-tracking/lists`);
      const data = res.data.lists || [];
      setLists(data);
      if (data.length > 0 && !activeListId) {
        setActiveListId(data[0].id);
      }
    } catch (e) {
      toast.error('Failed to load tracking lists');
    } finally {
      setLoadingLists(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchLists(); }, [fetchLists]);

  useEffect(() => {
    if (activeListId) {
      const found = lists.find(l => l.id === activeListId);
      setActiveList(found || null);
      if (!skipTabResetRef.current) {
        setActiveTabIndex(0);
      }
      skipTabResetRef.current = false;
    } else {
      setActiveList(null);
    }
  }, [activeListId, lists]);

  const handleCreateList = async (name, branches) => {
    try {
      const res = await api.post(`${API}/project-tracking/lists`, { name, branches });
      const newList = res.data.list;
      setLists(prev => [...prev, newList]);
      setActiveListId(newList.id);
      setShowCreateList(false);
      toast.success(`"${name}" tracking list created`);
    } catch (e) {
      toast.error('Failed to create list');
    }
  };

  const handleUpdateList = async (listId, updates) => {
    try {
      const res = await api.put(`${API}/project-tracking/lists/${listId}`, updates);
      const updated = res.data.list;
      setLists(prev => prev.map(l => l.id === listId ? updated : l));
      setEditingList(null);
      toast.success('List updated');
    } catch (e) {
      toast.error('Failed to update list');
    }
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Delete this tracking list? This cannot be undone.')) return;
    try {
      await api.delete(`${API}/project-tracking/lists/${listId}`);
      const remaining = lists.filter(l => l.id !== listId);
      setLists(remaining);
      setActiveListId(remaining.length > 0 ? remaining[0].id : null);
      toast.success('List deleted');
    } catch (e) {
      toast.error('Failed to delete list');
    }
  };

  const handleAddProjects = async (projectIds) => {
    if (!activeListId) return;
    try {
      const prevIds = activeList?.project_ids || [];
      const res = await api.post(`${API}/project-tracking/lists/${activeListId}/projects`, { project_ids: projectIds });
      const updated = res.data.list;
      // Navigate to the first newly added project's tab
      const newIds = updated.project_ids || [];
      const firstNewIdx = newIds.findIndex(id => !prevIds.includes(id));
      if (firstNewIdx >= 0) {
        skipTabResetRef.current = true; // prevent useEffect from resetting back to tab 0
        setActiveTabIndex(firstNewIdx);
      }
      setLists(prev => prev.map(l => l.id === activeListId ? updated : l));
      toast.success(`${projectIds.length} project(s) added to list`);
      setShowSearch(false);
    } catch (e) {
      toast.error('Failed to add projects');
    }
  };

  const handleRemoveProject = async (projectId) => {
    if (!activeListId) return;
    try {
      const res = await api.delete(`${API}/project-tracking/lists/${activeListId}/projects/${projectId}`);
      const updated = res.data.list;
      setLists(prev => prev.map(l => l.id === activeListId ? updated : l));
      toast.success('Project removed from list');
    } catch (e) {
      toast.error('Failed to remove project');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        user={user}
        onLogout={onLogout}
        scrollWithPage
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Project Tracking</span>
          </div>
        }
      />

      <div className="pt-4 flex-1 min-h-0">
          {loadingLists ? (
            <div className="flex items-center justify-center h-64 text-gray-400 text-sm">Loading tracking lists…</div>
          ) : lists.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="text-5xl">📋</div>
              <p className="text-gray-600 font-medium text-lg">No tracking lists yet</p>
              <p className="text-gray-400 text-sm">Create a list to start tracking CRM projects</p>
              {(user?.role === 'super_admin' || user?.can_add_project_tracking_lists) && (
                <button
                  onClick={() => setShowCreateList(true)}
                  className="mt-2 px-6 py-2.5 text-white rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#ee7203' }}
                  data-testid="pt-create-first-list-btn"
                >
                  Create First List
                </button>
              )}
            </div>
          ) : !activeList || (activeList.project_ids || []).length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <div className="text-4xl">🔍</div>
              <p className="text-gray-600 font-medium">No projects in this list</p>
              <button
                onClick={() => setShowSearch(true)}
                className="px-5 py-2 text-white rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#ee7203' }}
                data-testid="pt-add-first-project-btn"
              >
                Add Projects
              </button>
            </div>
          ) : (
            <ProjectTabView
              list={activeList}
              user={user}
              activeTabIndex={activeTabIndex}
              onTabChange={setActiveTabIndex}
              onRemoveProject={handleRemoveProject}
              onAddProjects={() => setShowSearch(true)}
              onProjectsDataChange={setProjectsData}
              lists={lists}
              activeListId={activeListId}
              onSelectList={(id) => { setActiveListId(id); }}
              onNewList={() => setShowCreateList(true)}
              onEditList={(list) => setEditingList(list)}
              onDeleteList={handleDeleteList}
              activeList={activeList}
            />
          )}
        </div>

      {showSearch && (
        <ProjectSearchModal
          isOpen={showSearch}
          onClose={() => setShowSearch(false)}
          onAdd={handleAddProjects}
          existingIds={activeList?.project_ids || []}
        />
      )}

      {(showCreateList || editingList) && (
        <CreateListModal
          isOpen={showCreateList || !!editingList}
          onClose={() => { setShowCreateList(false); setEditingList(null); }}
          onSave={editingList
            ? (name, branches) => handleUpdateList(editingList.id, { name, branches })
            : handleCreateList
          }
          initial={editingList}
        />
      )}
    </div>
  );
}
